import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DetailPanel = ({ selectedYard, onClose }) => {
  if (!selectedYard) {
    return (
      <div className="w-80 bg-card border-l border-border flex flex-col items-center justify-center p-8">
        <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
          <Icon name="MapPin" size={32} color="var(--color-muted-foreground)" strokeWidth={2} />
        </div>
        <p className="text-sm text-muted-foreground text-center">
          Select a yard to view details
        </p>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-success bg-success/10';
      case 'inactive':
        return 'text-muted-foreground bg-muted';
      case 'maintenance':
        return 'text-warning bg-warning/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  return (
    <div className="w-80 bg-card border-l border-border flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground">Yard Details</h3>
        <button
          onClick={onClose}
          className="flex items-center justify-center w-8 h-8 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-md transition-hover"
          title="Close details"
        >
          <Icon name="X" size={20} strokeWidth={2} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Basic Information</h4>
            <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md ${getStatusColor(selectedYard?.status)}`}>
              {selectedYard?.status?.charAt(0)?.toUpperCase() + selectedYard?.status?.slice(1)}
            </span>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Yard ID</label>
              <p className="text-sm font-medium text-foreground">{selectedYard?.yardId}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Yard Name</label>
              <p className="text-sm text-foreground">{selectedYard?.name}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Address</label>
              <p className="text-sm text-foreground">{selectedYard?.address}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Region</label>
              <p className="text-sm text-foreground">{selectedYard?.region}</p>
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Capacity & Utilization</h4>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Total Capacity</label>
              <p className="text-sm font-medium text-foreground">{selectedYard?.capacity} units</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Current Units</label>
              <p className="text-sm text-foreground">{selectedYard?.currentUnits} units</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Utilization Rate</label>
              <div className="flex items-center space-x-2">
                <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full ${selectedYard?.utilization >= 80 ? 'bg-error' : selectedYard?.utilization >= 50 ? 'bg-warning' : 'bg-success'}`}
                    style={{ width: `${selectedYard?.utilization}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium text-foreground">{selectedYard?.utilization}%</span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Manager Contact</h4>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Manager Name</label>
              <p className="text-sm text-foreground">{selectedYard?.manager}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Phone Number</label>
              <a
                href={`tel:${selectedYard?.managerPhone}`}
                className="text-sm text-primary hover:underline"
              >
                {selectedYard?.managerPhone}
              </a>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Email</label>
              <a
                href={`mailto:${selectedYard?.managerEmail}`}
                className="text-sm text-primary hover:underline break-all"
              >
                {selectedYard?.managerEmail}
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Operational Details</h4>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Operating Hours</label>
              <p className="text-sm text-foreground">{selectedYard?.operatingHours}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Last Inspection</label>
              <p className="text-sm text-foreground">{selectedYard?.lastInspection}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Next Maintenance</label>
              <p className="text-sm text-foreground">{selectedYard?.nextMaintenance}</p>
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">GPS Integration</h4>
          <div className="flex items-center space-x-2 px-3 py-2 bg-success/10 text-success text-xs font-medium rounded-md">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
            <span>GPS Tracking Active</span>
          </div>
        </div>
      </div>
      <div className="px-4 py-3 border-t border-border space-y-2">
        <Button
          variant="default"
          fullWidth
          iconName="Edit2"
          iconPosition="left"
        >
          Edit Yard
        </Button>
        <Button
          variant="outline"
          fullWidth
          iconName="FileText"
          iconPosition="left"
        >
          Generate Report
        </Button>
      </div>
    </div>
  );
};

export default DetailPanel;